import * as notifier from './notifier.js';
import * as loader from './loader.js';
import * as types from './types/signup.js';
import * as api from '../xhr/signup.js';
import * as session from '../services/session.js';
import { i18n, tokens } from '@simplybenefits/sb-ui-library';
import * as whitelabelTypes from "./types/whitelabel.js";

/**
 * @param dispatch
 * @param isLoading
 * @param type
 */
function setLoader(dispatch, isLoading, type = 'full') {
    dispatch(loader.setLoader({
        isLoading,
        type
    }));
}

/**
 * @param accountID
 * @returns {function(*): Promise<{initData: *, savedData: *|null} | boolean>}
 */
export function getWhitelabel(accountID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return api.getWhitelabel(accountID)
            .then(async (data) => {
                setLoader(dispatch, false);

                // save the whitelabel in local storage for if they logout
                await session.saveWhitelabel(data);

                dispatch({
                    type: whitelabelTypes.SAVE_WHITE_LABEL,
                    data
                });

                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                return false;
            });
    };
}

/**
 * @param affiliateLinkID
 */
export function validateAffiliateLink(affiliateLinkID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return api.validateAffiliateLink(affiliateLinkID)
            .then((accountID) => accountID)
            .catch((error) => {
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param userID
 * @param accountID
 * @returns {function(*=): Promise<boolean | never>}
 */
export function getSignupData(userID, accountID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return api.getSignupData(userID, accountID)
            .then((response) => {
                setLoader(dispatch, false);

                const savedEnrollment = session.getEnrollmentData(userID);

                if (!savedEnrollment) {
                    dispatch({
                        type: types.SAVE_INIT_DATA,
                        data: {
                            ...response
                        }
                    });
                } else {
                    dispatch({
                        type: types.SAVE_EXPECTED_DATA,
                        data: {
                            response,
                            savedEnrollment
                        }
                    });
                }

                return {
                    initData: response,
                    savedData: savedEnrollment
                };
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param data
 * @returns {function(*=)}
 */
export function signup(data) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return api
            .signup(data)
            .then(() => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage(i18n.t(tokens.labels.signup.accountCreatedSuccessfully)));

                session.setUserLanguage(data.userDetails.language);
                session.removeEnrollmentData(data.userDetails.userID);

                setTimeout(() => {
                    window.location.href = '/login';
                }, 500);
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param data
 * @returns {function(*): Promise<unknown>}
 */
export function marketplaceOnlySignup(data) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return api
            .marketplaceOnlySignup(data)
            .then(() => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Account created successfully!'));

                session.removeEnrollmentData(data.userDetails.userID);

                setTimeout(() => {
                    window.location.href = '/login';
                }, 500);
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param userID
 * @param accountID
 * @param language
 * @returns {function(*): Promise<*>}
 */
export function initStripe(userID, accountID, language) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return api
            .initStripe(userID, accountID, language)
            .then((data) => data)
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * Store the form value in state for the signup form steppers
 * @param section
 * @param key
 * @param value
 * @returns {Function}
 */
export function updateFormField(section, key, value) {
    return (dispatch) => {
        dispatch({
            type: types.SET_FORM_FIELD,
            section,
            key,
            value
        });

        dispatch({
            type: types.SAVE_LOCAL_COPY
        });
    };
}

/**
 *
 * @param {*} dependent
 */
export function addDependentToList(dependent) {
    return (dispatch) => {
        dispatch({
            type: types.ADD_DEPENDENT,
            dependent
        });

        dispatch({
            type: types.SAVE_LOCAL_COPY
        });
    };
}

/**
 *
 * @param {*} index
 * @param {*} dependent
 */
export function editDependentInList(index, dependent) {
    return (dispatch) => {
        dispatch({
            type: types.EDIT_DEPENDENT,
            dependent,
            index
        });

        dispatch({
            type: types.SAVE_LOCAL_COPY
        });
    };
}

/**
 *
 * @param {*} index
 */
export function removeDependentFromList(index) {
    return (dispatch) => {
        dispatch({
            type: types.REMOVE_DEPENDENT,
            index
        });

        dispatch({
            type: types.SAVE_LOCAL_COPY
        });
    };
}

/**
 * @param beneficiary
 * @param isContingent
 * @returns {Function}
 */
export function addBeneficiaryToList(beneficiary, isContingent = false) {
    return (dispatch) => {
        dispatch({
            type: types.ADD_BENEFICIARY,
            beneficiary,
            isContingent
        });

        dispatch({
            type: types.SAVE_LOCAL_COPY
        });
    };
}

/**
 * @param index
 * @param beneficiary
 * @param isContingent
 * @returns {Function}
 */
export function editBeneficiaryInList(index, beneficiary, isContingent = false) {
    return (dispatch) => {
        dispatch({
            type: types.EDIT_BENEFICIARY,
            beneficiary,
            index,
            isContingent
        });

        dispatch({
            type: types.SAVE_LOCAL_COPY
        });
    };
}

/**
 * @param index
 * @param isContingent
 * @returns {Function}
 */
export function removeBeneficiaryFromList(index, isContingent = false) {
    return (dispatch) => {
        dispatch({
            type: types.REMOVE_BENEFICIARY,
            index,
            isContingent
        });

        dispatch({
            type: types.SAVE_LOCAL_COPY
        });
    };
}

/**
 * Resets the signup state by dispatching the RESET_SIGNUP action.
 *
 * @param {Function} dispatch - The Redux dispatch function.
 * @returns {void} The function returns undefined.
 */
export function resetSignup() {
    return (dispatch) => {
        dispatch({
            type: types.RESET_SIGNUP
        });
    };
}